import React, { useState, useEffect, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Autocomplete,
} from "@react-google-maps/api";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import classes from "./location.module.css";
import { FaArrowLeft } from "react-icons/fa";
import { IoIosArrowForward } from "react-icons/io";
import { authApi } from "../../../api-calls/user-requests";

// Must be defined outside component to avoid re-render issues with useJsApiLoader
const LIBRARIES = ["places"];

const AddressMapLocation = ({
  mapLocation,
  setMapLocation,
  setIsMapScreen,
}) => {
  const [marker, setMarker] = useState(null);
  const [centerPoint, setCenterPoint] = useState({ lat: 20.5937, lng: 78.9629 });
  const [locality, setLocality] = useState("");
  const [address, setAddress] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [urlLoading, setUrlLoading] = useState(false);

  const autocompleteRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    libraries: LIBRARIES,
  });

  useEffect(() => {
    if (mapLocation) {
      try {
        const url = new URL(mapLocation);
        const [lat, lng] = url.search.replace("?q=", "").split(",").map(Number);
        if (lat && lng) {
          setMarker({ lat, lng });
          setCenterPoint({ lat, lng });
          fetchAddress(lat, lng);
        }
      } catch (e) {}
    } else if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setCenterPoint({ lat: latitude, lng: longitude });
        },
        () => {}
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLocalityFromComponents = (addressComponents, formattedAddress) => {
    const localityTypes = [
      "locality",
      "sublocality_level_1",
      "sublocality",
      "neighborhood",
      "administrative_area_level_3",
      "administrative_area_level_2",
    ];
    for (const type of localityTypes) {
      const comp = addressComponents.find((c) => c.types.includes(type));
      if (comp) return comp.long_name;
    }
    return formattedAddress ? formattedAddress.split(",")[0].trim() : "Unknown locality";
  };

  const fetchAddress = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${process.env.REACT_APP_GOOGLE_MAP_KEY}`
      );
      const data = await response.json();
      if (data.results && data.results.length > 0) {
        const result = data.results[0];
        setAddress(result.formatted_address);
        setLocality(
          getLocalityFromComponents(
            result.address_components,
            result.formatted_address
          )
        );
      } else {
        setAddress("Address not found");
        setLocality("Unknown locality");
      }
    } catch (error) {
      console.error("Error fetching address:", error);
      setAddress("Error fetching address");
      setLocality("Error fetching locality");
    }
  };

  const handleMapClick = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setMarker({ lat, lng });
    setMapLocation(`https://www.google.com/maps?q=${lat},${lng}`);
    fetchAddress(lat, lng);
  };

  const handlePlaceChanged = () => {
    const place = autocompleteRef.current.getPlace();
    if (!place.geometry) return;
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    setCenterPoint({ lat, lng });
    setMarker({ lat, lng });
    setMapLocation(`https://www.google.com/maps?q=${lat},${lng}`);
    if (place.address_components) {
      setAddress(place.formatted_address || "");
      setLocality(
        getLocalityFromComponents(
          place.address_components,
          place.formatted_address
        )
      );
    } else {
      fetchAddress(lat, lng);
    }
  };

  const extractCoordsFromUrl = (url) => {
    const patterns = [
      /@(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/,
      /!3d(-?\d+\.?\d*)!4d(-?\d+\.?\d*)/,
    ];
    for (const pattern of patterns) {
      const m = url.match(pattern);
      if (m) return { lat: parseFloat(m[1]), lng: parseFloat(m[2]) };
    }
    return null;
  };

  const handleUrlChange = async (value) => {
    setUrlInput(value);
    const trimmed = value.trim();
    if (!trimmed) return;

    if (trimmed.includes("maps.app.goo.gl") || trimmed.includes("goo.gl/maps")) {
      setUrlLoading(true);
      try {
        const res = await authApi.post("/locations/resolve-short-url", {
          shortUrl: trimmed,
        });
        if (res.data.success && res.data.coordinates) {
          const { latitude, longitude } = res.data.coordinates;
          setMarker({ lat: latitude, lng: longitude });
          setCenterPoint({ lat: latitude, lng: longitude });
          setMapLocation(
            `https://www.google.com/maps?q=${latitude},${longitude}`
          );
          fetchAddress(latitude, longitude);
        }
      } catch (e) {
        console.error("Error resolving short URL:", e);
      }
      setUrlLoading(false);
      return;
    }

    const coords = extractCoordsFromUrl(trimmed);
    if (coords) {
      setMarker(coords);
      setCenterPoint(coords);
      setMapLocation(`https://www.google.com/maps?q=${coords.lat},${coords.lng}`);
      fetchAddress(coords.lat, coords.lng);
    }
  };

  return (
    <Box sx={{ position: "relative", display: "flex", flexDirection: "column" }}>
      <div>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <FaArrowLeft
            color="black"
            style={{ cursor: "pointer" }}
            onClick={() => setIsMapScreen(false)}
          />
          <span>Assign a Task</span>
          <IoIosArrowForward
            color="black"
            size={"14px"}
            style={{ marginTop: "2px" }}
          />
          <span style={{ color: "#646569" }}>Add from Map</span>
        </Box>

        {/* Search box with Places Autocomplete dropdown */}
        {isLoaded && (
          <Box sx={{ mb: 1 }}>
            <Autocomplete
              onLoad={(ref) => (autocompleteRef.current = ref)}
              onPlaceChanged={handlePlaceChanged}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Search for a location..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Autocomplete>
          </Box>
        )}

        {/* Map */}
        <div style={{ position: "relative" }}>
          {isLoaded ? (
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "400px" }}
              center={marker || centerPoint}
              zoom={10}
              onClick={handleMapClick}
            >
              {marker && <Marker position={marker} />}
            </GoogleMap>
          ) : (
            <p>Loading Map...</p>
          )}
          <div className={classes.addressmap_container}>
            <h5>{locality || "n/a"}</h5>
            <p>{address || "n/a"}</p>
            <Button
              variant="contained"
              onClick={() => setIsMapScreen(false)}
              disabled={!marker}
              className={classes.addressmap_button}
            >
              Add Location
            </Button>
          </div>
        </div>

        {/* Google Maps URL paste */}
        <Box sx={{ mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Or paste a Google Maps link (maps.app.goo.gl/...)"
            value={urlInput}
            onChange={(e) => handleUrlChange(e.target.value)}
            InputProps={{
              endAdornment: urlLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      </div>
    </Box>
  );
};

export default AddressMapLocation;
